import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/admin/DashboardLayout';
import { 
  Trash2, Image as ImageIcon, Film, Search, 
  AlertTriangle, Check, X, Loader2, Minimize2, 
  MousePointer2, CheckSquare, ChevronDown
} from 'lucide-react';
import { useNotification } from '../../context/NotificationContext';

const LazyImage = ({ src, alt, style }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <>
      <div 
        style={{
          ...style, 
          backgroundColor: '#f3f4f6', 
          position: 'absolute', 
          top: 0, left: 0, width: '100%', height: '100%',
          opacity: loaded ? 0 : 1,
          transition: 'opacity 0.5s ease'
        }} 
      />
      <img 
        src={src} 
        alt={alt} 
        loading="lazy"
        onLoad={() => setLoaded(true)}
        style={{
          ...style,
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.5s ease'
        }}
      />
    </>
  );
};

const MediaLibrary = () => {
  const { addNotification } = useNotification();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search & Selection
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [visibleCount, setVisibleCount] = useState(50); // Pagination state

  // Modal States
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteUsages, setDeleteUsages] = useState({}); // Map filename -> usage list
  const [deleting, setDeleting] = useState(false);

  const [showCompressModal, setShowCompressModal] = useState(false);
  
  // Compressor States
  const [compressionQuality, setCompressionQuality] = useState(0.7);
  const [compressing, setCompressing] = useState(false);
  const [compressProgress, setCompressProgress] = useState(0);
  const [processedFiles, setProcessedFiles] = useState([]);

  useEffect(() => {
    fetchMedia();
  }, []);

  // Helper: Format Bytes
  const formatBytes = (bytes, decimals = 2) => {
      if (!+bytes) return '0 Bytes';
      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  const fetchMedia = async () => {
    setLoading(true);
    try {
      // Fetch with high limit to keep client-side features working for now
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/media.php?limit=1000`);
      
      if (res.data.data && Array.isArray(res.data.data)) {
        setFiles(res.data.data);
      } else if (Array.isArray(res.data)) {
        setFiles(res.data);
      } else {
        setFiles([]);
      }
    } catch (err) {
      addNotification('Gagal memuat media library', 'error');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  // --- SELECTION LOGIC ---
  
  const toggleSelection = (filename) => {
    const newSet = new Set(selectedFiles);
    if (newSet.has(filename)) {
      newSet.delete(filename);
    } else {
      newSet.add(filename);
    }
    setSelectedFiles(newSet);
  };

  const clearSelection = () => setSelectedFiles(new Set());

  // --- BULK DELETE LOGIC ---

  const handleDeleteClick = async () => {
    if (selectedFiles.size === 0) return;
    
    setShowDeleteModal(true);
    setDeleting(true); // "Checking..." state
    setDeleteUsages({});

    // Check usages in parallel
    const usageMap = {};
    const checkPromises = Array.from(selectedFiles).map(async (filename) => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/media.php?action=check_usage&filename=${filename}`);
        if (res.data.usages && res.data.usages.length > 0) {
          usageMap[filename] = res.data.usages;
        }
      } catch (err) {
        // ignore error
      }
    });

    await Promise.all(checkPromises);
    setDeleteUsages(usageMap);
    setDeleting(false); // Ready to confirm
  };

  const confirmDelete = async () => {
    setDeleting(true); // "Deleting..." state
    try {
      const deletePromises = Array.from(selectedFiles).map(filename => 
        axios.delete(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/media.php?filename=${filename}&force=true`)
      );
      
      await Promise.all(deletePromises);
      addNotification(`${selectedFiles.size} file berhasil dihapus`, 'success');
      
      // Refresh
      setFiles(prev => prev.filter(f => !selectedFiles.has(f.name)));
      setSelectedFiles(new Set());
      setShowDeleteModal(false);
    } catch (err) {
      addNotification('Gagal menghapus beberapa file', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // --- BATCH COMPRESS LOGIC ---

  const handleCompressClick = async () => {
    const selectedArray = Array.from(selectedFiles);
    
    // Filter only images
    const imagesToCompress = files.filter(f => selectedFiles.has(f.name) && f.type === 'image');
    
    if (imagesToCompress.length === 0) {
      addNotification('Pilih minimal 1 gambar', 'error');
      return;
    }
    if (imagesToCompress.length > 5) {
      addNotification('Maksimal 5 gambar untuk 1 batch', 'error');
      return;
    }

    setProcessedFiles([]);
    setShowCompressModal(true);
    
    // Prepare previews
    const prepared = imagesToCompress.map(f => ({
      ...f,
      originalSize: f.size, // Map size from backend response
      status: 'pending' // pending, compressing, done
    }));
    setProcessedFiles(prepared);
  };

  const runCompression = async () => {
    setCompressing(true);
    setCompressProgress(0);
    
    const total = processedFiles.length;
    const results = [];

    for (let i = 0; i < total; i++) {
      const item = processedFiles[i];
      
      try {
        // Use Proxy to fetch blob with CORS headers
        // item.relative_path comes from media.php
        // If relative_path is missing, try to deduce it or fail gracefully
        const pathParam = item.relative_path || item.name; 
        const proxyUrl = `${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/proxy_image.php?path=${encodeURIComponent(pathParam)}`;
        
        const response = await fetch(proxyUrl);
        if (!response.ok) throw new Error('Network response was not ok');
        
        const blob = await response.blob();
        
        // Compress
        const compressedBlob = await compressBlob(blob, compressionQuality);
        
        results.push({
          ...item,
          processedBlob: compressedBlob,
          processedPreview: URL.createObjectURL(compressedBlob),
          processedSize: compressedBlob.size,
          originalSize: blob.size,
          status: 'done'
        });

      } catch (err) {
        console.error(err);
        results.push({ ...item, status: 'error' });
      }

      // Update Progress
      setCompressProgress(Math.round(((i + 1) / total) * 100));
    }

    setProcessedFiles(results);
    setCompressing(false);
  };

  const compressBlob = (blob, quality) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      // Fix for Tainted Canvas / CORS issues
      img.crossOrigin = "Anonymous"; 
      img.src = URL.createObjectURL(blob);
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(res => resolve(res), 'image/jpeg', quality);
      };
      img.onerror = err => reject(err);
    });
  };

  const saveCompressed = async (item, mode) => { // mode: 'new' or 'overwrite'
    const formData = new FormData();
    
    // Re-create File object
    const newName = mode === 'overwrite' ? item.name : item.name.replace(/(\.[\w\d]+)$/, '_cmp$1');
    const file = new File([item.processedBlob], newName, { type: 'image/jpeg' });
    
    formData.append('image', file);
    formData.append('mode', mode);
    formData.append('original_name', item.name);
    formData.append('target_path', item.relative_path || item.name); // Use relative path if available

    try {
      await axios.post(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/save_image.php`, formData);
      addNotification(mode === 'overwrite' ? 'Gambar ditimpa' : 'Disimpan sebagai file baru', 'success');
      
      // Mark as saved in UI?
      // Refresh library if finished all
      if (mode === 'overwrite') {
          // Force refresh to show new size/date?
           fetchMedia(); 
      } else {
           fetchMedia(); 
      }
    } catch (err) {
      addNotification('Gagal menyimpan', 'error');
    }
  };

  // --- UI ---

  const filteredFiles = files.filter(f => f.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const visibleFiles = filteredFiles.slice(0, visibleCount);

  // Reset pagination when search changes
  useEffect(() => {
    setVisibleCount(50);
  }, [searchTerm]);

  const styles = {
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' },
    actionBar: {
      display: 'flex', gap: '1rem', padding: '1rem', backgroundColor: '#fff', 
      borderBottom: '1px solid #e5e7eb', alignItems: 'center', position: 'sticky', top: 0, zIndex: 40, marginBottom: '1.5rem', borderRadius: '0.5rem'
    },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '1rem' },
    card: (selected) => ({
      backgroundColor: '#fff', borderRadius: '0.5rem', border: selected ? '2px solid #2563eb' : '1px solid #e5e7eb', overflow: 'hidden',
      position: 'relative', aspectRatio: '1/1', cursor: 'pointer', transition: 'all 0.2s'
    }),
    img: { width: '100%', height: '100%', objectFit: 'cover' },
    checkbox: {
      position: 'absolute', top: '0.5rem', left: '0.5rem', zIndex: 10,
      width: '20px', height: '20px', accentColor: '#2563eb', cursor: 'pointer'
    },
    modalOverlay: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100,
      backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center'
    },
    modal: {
      backgroundColor: '#fff', padding: '2rem', borderRadius: '1rem', maxWidth: '600px', width: '90%', maxHeight: '85vh', overflowY: 'auto'
    },
    btn: (variant) => ({
      padding: '0.6rem 1.2rem', borderRadius: '0.4rem', border: 'none', fontWeight: '600', cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem',
      backgroundColor: variant === 'danger' ? '#dc2626' : variant === 'primary' ? '#111' : '#e5e7eb',
      color: variant === 'danger' || variant === 'primary' ? '#fff' : '#374151'
    })
  };

  return (
    <DashboardLayout>
      <div style={styles.header}>
        <h1 style={{fontSize: '1.5rem', fontWeight: '700'}}>Media Library</h1>
        <div style={{display:'flex', gap:'1rem', alignItems:'center'}}>
           <div style={{display: 'flex', alignItems: 'center', backgroundColor: '#fff', padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid #e5e7eb'}}>
              <Search size={18} color="#6b7280" style={{marginRight: '0.5rem'}} />
              <input 
                  placeholder="Cari file..." 
                  style={{border: 'none', outline: 'none', fontSize: '0.9rem'}}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
              />
           </div>
        </div>
      </div>

      {/* ACTION BAR (Visible when selecting) */}
      {selectedFiles.size > 0 && (
        <div style={styles.actionBar}>
          <div style={{fontWeight:'600', marginRight:'auto', display:'flex', alignItems:'center', gap:'0.5rem'}}>
             <CheckSquare size={20} color="#2563eb"/> {selectedFiles.size} terpilih
          </div>
          <button onClick={clearSelection} style={styles.btn('secondary')}>Batal</button>
          
          <button onClick={handleCompressClick} style={styles.btn('primary')}>
            <Minimize2 size={18}/> Compress ({Array.from(selectedFiles).filter(n => files.find(f => f.name===n)?.type === 'image').length})
          </button>
          
          <button onClick={handleDeleteClick} style={styles.btn('danger')}>
            <Trash2 size={18}/> Hapus
          </button>
        </div>
      )}

      {loading ? (
         <div style={{textAlign:'center', padding:'3rem', color:'#6b7280'}}><Loader2 className="animate-spin"/></div>
      ) : (
        <>
          <div style={styles.grid}>
              {visibleFiles.map((file, idx) => (
                  <div 
                      key={idx} 
                      style={styles.card(selectedFiles.has(file.name))}
                      onClick={() => toggleSelection(file.name)}
                  >
                      <input 
                        type="checkbox" 
                        checked={selectedFiles.has(file.name)} 
                        onChange={() => {}} // Handled by div click
                        style={styles.checkbox}
                      />
                      
                      {file.type === 'video' ? (
                          <video src={file.url} style={styles.img} />
                      ) : (
                          <LazyImage src={file.url} alt={file.name} style={styles.img} />
                      )}
                      
                      <div style={{position:'absolute', bottom:0, left:0, right:0, background:'rgba(0,0,0,0.6)', color:'#fff', fontSize:'0.7rem', padding:'0.25rem 0.5rem', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                        {file.name}
                      </div>
                  </div>
              ))}
          </div>

          {/* Load More Button */}
          {visibleCount < filteredFiles.length && (
            <div style={{textAlign: 'center', marginTop: '2rem', paddingBottom: '2rem'}}>
              <button 
                onClick={() => setVisibleCount(prev => prev + 50)}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '2rem',
                  color: '#374151',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  transition: 'all 0.2s'
                }}
              >
                Load More <ChevronDown size={16} />
              </button>
              <div style={{fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.5rem'}}>
                Menampilkan {visibleFiles.length} dari {filteredFiles.length} file
              </div>
            </div>
          )}
        </>
      )}

      {/* DELETE MODAL */}
      {showDeleteModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
             <h3 style={{fontSize:'1.25rem', fontWeight:'700', marginBottom:'1rem', color:'#991b1b'}}>Konfirmasi Hapus Massal</h3>
             
             <p style={{marginBottom:'1.5rem'}}>Anda akan menghapus <b>{selectedFiles.size} items</b>. Tindakan ini tidak dapat dibatalkan.</p>

             {/* Usage Warnings */}
             {Object.keys(deleteUsages).length > 0 && (
               <div style={{backgroundColor:'#fef2f2', padding:'1rem', borderRadius:'0.5rem', marginBottom:'1.5rem', border:'1px solid #fecaca', maxHeight:'200px', overflowY:'auto'}}>
                  <div style={{fontWeight:'600', color:'#991b1b', marginBottom:'0.5rem', display:'flex', alignItems:'center', gap:'0.5rem'}}>
                     <AlertTriangle size={16}/> {Object.keys(deleteUsages).length} File Sedang Digunakan:
                  </div>
                  {Object.entries(deleteUsages).map(([filename, usages]) => (
                    <div key={filename} style={{marginBottom:'0.5rem', fontSize:'0.85rem'}}>
                       <b>{filename}:</b>
                       <ul style={{margin:'0.25rem 0', paddingLeft:'1rem', color:'#b91c1c'}}>
                          {usages.map((u, i) => <li key={i}>{u}</li>)}
                       </ul>
                    </div>
                  ))}
               </div>
             )}

             <div style={{display:'flex', justifyContent:'flex-end', gap:'1rem'}}>
               <button onClick={() => setShowDeleteModal(false)} style={styles.btn('secondary')}>Batal</button>
               <button onClick={confirmDelete} style={styles.btn('danger')} disabled={deleting}>
                 {deleting ? <Loader2 className="animate-spin"/> : 'Ya, Hapus Semua'}
               </button>
             </div>
          </div>
        </div>
      )}

      {/* COMPRESS MODAL */}
      {showCompressModal && (
        <div style={styles.modalOverlay}>
           <div style={styles.modal}>
              <div style={{display:'flex', justifyContent:'space-between', marginBottom:'1.5rem'}}>
                <h3 style={{fontSize:'1.25rem', fontWeight:'700'}}>Batch Compressor</h3>
                <button onClick={() => setShowCompressModal(false)} style={{background:'none', border:'none', cursor:'pointer'}}><X/></button>
              </div>

              {!processedFiles[0]?.processedBlob && (
                <div style={{marginBottom:'2rem', padding:'1rem', backgroundColor:'#f9fafb', borderRadius:'0.5rem'}}>
                   <label style={{display:'block', fontWeight:'600', marginBottom:'0.5rem'}}>Kualitas: {Math.round(compressionQuality * 100)}%</label>
                   <input 
                      type="range" min="0.1" max="1.0" step="0.1" 
                      value={compressionQuality} 
                      onChange={e => setCompressionQuality(parseFloat(e.target.value))}
                      style={{width:'100%'}}
                   />
                   <div style={{marginTop:'1rem'}}>
                      <button onClick={runCompression} disabled={compressing} style={{width:'100%', ...styles.btn('primary'), justifyContent:'center'}}>
                        {compressing ? 'Memproses...' : 'Mulai Kompresi'}
                      </button>
                   </div>
                </div>
              )}

              {/* Progress Bar */}
              {compressing && (
                <div style={{marginBottom:'1.5rem'}}>
                   <div style={{height:'8px', width:'100%', backgroundColor:'#e5e7eb', borderRadius:'4px', overflow:'hidden'}}>
                      <div style={{height:'100%', width:`${compressProgress}%`, backgroundColor:'#2563eb', transition:'width 0.3s'}}></div>
                   </div>
                   <div style={{textAlign:'center', fontSize:'0.8rem', marginTop:'0.5rem', color:'#6b7280'}}>{compressProgress}% Selesai</div>
                </div>
              )}

              {/* Results List */}
              <div style={{maxHeight:'400px', overflowY:'auto'}}>
                 {processedFiles.map((item, idx) => (
                   <div key={idx} style={{display:'flex', gap:'1rem', padding:'1rem', border:'1px solid #e5e7eb', borderRadius:'0.5rem', marginBottom:'0.75rem'}}>
                      <img src={item.url} alt="Thumb" style={{width:'60px', height:'60px', objectFit:'cover', borderRadius:'0.25rem'}} />
                      
                      <div style={{flex:1}}>
                         <div style={{fontSize:'0.9rem', fontWeight:'600', marginBottom:'0.25rem'}}>{item.name}</div>
                         {item.status === 'done' ? (
                            <div style={{fontSize:'0.8rem', color:'#059669'}}>
                               {formatBytes(item.originalSize)} ➔ <b>{formatBytes(item.processedSize)}</b> ({Math.round((1 - item.processedSize/item.originalSize)*100)}% hemat)
                            </div>
                         ) : item.status === 'error' ? (
                            <div style={{fontSize:'0.8rem', color:'#dc2626', fontWeight:'600'}}>
                                Gagal memproses gambar.
                            </div>
                         ) : (
                            <div style={{fontSize:'0.8rem', color:'#6b7280'}}>
                                Original: <b>{formatBytes(item.originalSize)}</b> • Menunggu...
                            </div>
                         )}
                      </div>

                      {item.status === 'done' && (
                        <div style={{display:'flex', flexDirection:'column', gap:'0.5rem'}}>
                           <button onClick={() => saveCompressed(item, 'new')} style={{fontSize:'0.75rem', padding:'0.3rem 0.6rem', border:'1px solid #d1d5db', background:'#fff', borderRadius:'0.25rem', cursor:'pointer'}}>Simpan Baru</button>
                           <button onClick={() => saveCompressed(item, 'overwrite')} style={{fontSize:'0.75rem', padding:'0.3rem 0.6rem', border:'none', background:'#ef4444', color:'#fff', borderRadius:'0.25rem', cursor:'pointer'}}>Timpa</button>
                        </div>
                      )}
                   </div>
                 ))}
              </div>

           </div>
        </div>
      )}

    </DashboardLayout>
  );
};

export default MediaLibrary;
